import type { JSX, ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';

import { frozenDropAtom } from '../TimelineView/atoms';
import { FROZEN_DROP_PREVIEW_Z_INDEX } from '../frozenDrop.constants';
import { ZoomLevel as DetailZoomLevel } from '../components/Header/ZoomLevelMenu.enums';

import ChipDragPreviewPragmatic from './ChipDragPreviewPragmatic/ChipDragPreviewPragmatic';
import MultiDragPreviewPragmatic from './MultiDragPreviewPragmatic';

/**
 * Renders a frozen copy of the dropped card at the cursor while the plan/unassign
 * call resolves, replacing the vanished native drag ghost. Presentation only; the
 * lifecycle (set on drop, cleared on settle) is owned by `TimelineViewPragmatic`.
 */
const FrozenDropChipPragmatic = (): ReactPortal | JSX.Element | null => {
  'use memo';
  const frozenDrop = useRecoilValue(frozenDropAtom);

  if (!frozenDrop) return null;

  const { position, count, preview } = frozenDrop;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        // Center the preview under the drop point; sit just under LoadingOverlay.
        transform: 'translate(-50%, -50%)',
        zIndex: FROZEN_DROP_PREVIEW_Z_INDEX,
        pointerEvents: 'none'
      }}
    >
      {count > 1 ? (
        <MultiDragPreviewPragmatic count={count} />
      ) : (
        <ChipDragPreviewPragmatic
          width={preview.width}
          isClient={preview.isClient}
          isAbsent={preview.isAbsent}
          isRecurring={preview.isRecurring}
          selected={false}
          hasWarnings={preview.hasWarnings}
          detailZoomLevel={preview.detailZoomLevel ?? DetailZoomLevel.STANDARD}
          label={preview.label}
        />
      )}
    </div>,
    document.body
  );
};

export default FrozenDropChipPragmatic;
